import { useStorageTableColumns } from "PFApp/components/table/hooks/use_storage_table_columns";
import { TableColumn } from "PFApp/components/table/table_types";
import { getValueFilters, handleRequestFiltersChange } from "PFApp/use_filtered_collection/format_filters";
import { usePage } from "PFApp/use_page";
import useStorage from "PFCore/helpers/use_storage";
import { useBookingApprovals } from "PFCore/hooks/queries/bookings/approvals/use_booking_approvals";
import { usePreviousValue } from "PFCore/hooks/use_previous_value";
import { useCallback, useEffect, useState } from "react";

type IModalData = {
  data: any;
  actionType: "requestApproval" | "rejectApproval" | "delete" | "update" | "approve";
} | null;

const storageOptions = { expiresIn: 0 };
const placeholderData = { entries: [], meta: { filters: {} } };
const perPage = 10;
const initialOrder = {
  name: ["activity.saas_created_at"],
  text: "Latest first",
  direction: "desc"
};
export enum ColumnNames {
  booking = "booking",
  engagement = "engagement",
  role = "role",
  profile = "profile",
  creator = "creator",
  category = "category",
  approver = "approver",
  state = "state"
}
export const defaultColumns: TableColumn[] = [
  {
    name: ColumnNames.booking,
    attribute: ["booking", "title"],
    title: "booking",
    locked: true,
    size: 3,
    type: "text",
    orderKey: "booking.title"
  },
  {
    name: ColumnNames.engagement,
    attribute: ["activity", "engagement"],
    orderKey: "activity.engagement.name",
    title: "engagement",
    size: 3,
    type: "text"
  },
  {
    name: ColumnNames.role,
    attribute: ["activity"],
    title: "role",
    size: 3,
    type: "text",
    orderKey: "activity.name"
  },
  {
    name: ColumnNames.profile,
    title: "workforceMember",
    size: 2,
    type: "profile"
  },
  {
    name: ColumnNames.creator,
    title: "createdBy",
    size: 2,
    type: "profile"
  },
  {
    name: ColumnNames.category,
    attribute: ["booking", "booking_category_id"],
    title: "category",
    size: 2,
    type: "text"
  },
  {
    name: ColumnNames.approver,
    title: "approver",
    size: 2,
    type: "profile"
  },
  {
    name: ColumnNames.state,
    title: "status",
    size: 1,
    type: "text"
  }
];

const useApprovalsTab = () => {
  const { mergePage } = usePage("booking");

  const [filters, setFilters] = useState({});
  const [modalData, setModalData] = useState<IModalData>(null);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useStorage(
    `booking_approvals_selectedOrder`,
    initialOrder,
    storageOptions
  );
  const { selectedColumns, setSelectedColumns } = useStorageTableColumns(
    `booking_approvals_column_names`,
    defaultColumns,
    defaultColumns.filter(({ locked }) => locked)
  );

  const { isRefetching, data, isFetched } = useBookingApprovals({
    page,
    perPage,
    filters,
    order: selectedOrder
  });

  const previousData = usePreviousValue(data);

  const onFiltersReset = useCallback((filters = {}) => {
    setFilters(filters);
    setPage(1);
  }, []);

  const onFiltersChange = (filter, value) => {
    const valueFilters = getValueFilters(data.meta?.filters);
    const newFilters = handleRequestFiltersChange(valueFilters, filter, value);
    setFilters(newFilters);
  };

  useEffect(
    () =>
      mergePage({
        approvals: {
          data: data || previousData || placeholderData,
          onFiltersChange,
          onFilterChange: onFiltersChange,
          onFiltersReset,
          onOrderChange: setSelectedOrder,
          columns: selectedColumns,
          setColumns: setSelectedColumns,
          page,
          setPage,
          loading: !data || isRefetching,
          loaded: data || previousData,
          pinnedIds,
          setPinnedIds,
          openModal: setModalData,
          modalData
        }
      }),
    [data, selectedColumns, isRefetching, modalData, isFetched]
  );
};

export default useApprovalsTab;
